import { ICoordinates, ILocation } from 'src/types/graphql'

// Function to calculate distance between two coordinates in meters
const calculateDistance = (
  coord1: ICoordinates,
  coord2: ICoordinates
): number => {
  const R = 6371e3 // Earth's radius in meters
  const φ1 = (coord1.lat * Math.PI) / 180
  const φ2 = (coord2.lat * Math.PI) / 180
  const Δφ = ((coord2.lat - coord1.lat) * Math.PI) / 180
  const Δλ = ((coord2.lng - coord1.lng) * Math.PI) / 180

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

// Function to spread out markers that share the same coordinates
export const spreadMarkers = (
  markers: ILocation[],
  zoom: number
): ILocation[] => {
  const coordinateGroups = new Map<string, ILocation[]>()
  const GROUP_RADIUS = 20 // Group markers within 20 meters

  // Calculate base radius based on zoom level
  // At zoom level 22 (max zoom), radius is 0.0001 (11m)
  // At zoom level 1 (min zoom), radius is 0.004 (440m)
  const baseRadius = 0.0001 * Math.pow(2, (22 - zoom) / 2.5)

  // Group markers by their coordinates
  markers.forEach((marker) => {
    if (!marker.coordinates) return

    // Check if this marker is close to any existing group
    let addedToGroup = false
    for (const [key, group] of coordinateGroups.entries()) {
      const [lat, lng] = key.split(',').map(Number)
      const distance = calculateDistance({ lat, lng }, marker.coordinates)

      if (distance <= GROUP_RADIUS) {
        group.push(marker)
        addedToGroup = true
        break
      }
    }

    // If not added to any group, create a new one
    if (!addedToGroup) {
      const key = `${marker.coordinates.lat},${marker.coordinates.lng}`
      coordinateGroups.set(key, [marker])
    }
  })

  // Spread out markers that share coordinates
  const spreadMarkers: ILocation[] = []
  coordinateGroups.forEach((group: ILocation[]) => {
    if (group.length === 1) {
      spreadMarkers.push(group[0])
      return
    }

    // Calculate the center of the group
    const center = group.reduce(
      (acc, marker) => {
        if (!marker.coordinates) return acc
        return {
          lat: acc.lat + marker.coordinates.lat,
          lng: acc.lng + marker.coordinates.lng
        }
      },
      { lat: 0, lng: 0 }
    )

    center.lat /= group.length
    center.lng /= group.length

    // Calculate offsets for each marker in the group
    group.forEach((marker: ILocation, index: number) => {
      if (!marker.coordinates) return

      // Use a combination of horizontal and vertical offsets
      const angle = (index / group.length) * Math.PI * 2

      // Create a more organic pattern by varying the radius
      const radius = baseRadius * (0.8 + Math.random() * 0.4)

      // Calculate both horizontal and vertical offsets
      const offsetLat = Math.sin(angle) * radius
      const offsetLng = Math.cos(angle) * radius

      // Add a small random variation to both offsets
      const randomVariation = baseRadius * 0.4 // Increased random variation
      const finalOffsetLat = offsetLat + (Math.random() - 0.5) * randomVariation
      const finalOffsetLng = offsetLng + (Math.random() - 0.5) * randomVariation

      spreadMarkers.push({
        ...marker,
        coordinates: {
          lat: center.lat + finalOffsetLat,
          lng: center.lng + finalOffsetLng
        }
      })
    })
  })

  return spreadMarkers
}
